/**
 * Glossary Service
 * Story 13.2: Glossário - Frontend API Client
 *
 * Provides API calls for:
 * - Term listing with filters (category, letter, pagination)
 * - Full-text search with ranking
 * - Term details with relations and links
 * - Alphabet index (A-Z, 0-9, #)
 * - View increment
 * - Category listing
 * - Admin CRUD operations
 */
import api from "./api";

export interface GlossaryTerm {
  id: string;
  term: string;
  definition: string;
  context?: string;
  short_definition?: string;
  category_id?: string;
  category_name?: string;
  display_order: number;
  views: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface GlossaryTermDetail extends GlossaryTerm {
  related_terms: GlossaryRelation[];
  links: GlossaryLink[];
}

export interface GlossaryRelation {
  id: string;
  term_id: string;
  related_term_id: string;
  related_term_name: string;
  relation_type: "synonym" | "see_also" | "antonym";
  created_at: string;
}

export interface GlossaryLink {
  id: string;
  term_id: string;
  title: string;
  url: string;
  link_type: "faq" | "manual" | "external";
  created_at: string;
}

export interface GlossaryCategory {
  id: string;
  slug: string;
  name: string;
  description?: string;
  icon?: string;
  color?: string;
  display_order: number;
  term_count: number;
  created_at: string;
  updated_at: string;
}

export interface GlossarySearchResult {
  id: string;
  term: string;
  definition: string;
  short_definition?: string;
  category_name?: string;
  rank: number;
}

export interface AlphabetIndexItem {
  letter: string;
  count: number;
}

export interface GlossaryTermCreate {
  term: string;
  definition: string;
  context?: string;
  short_definition?: string;
  category_id?: string;
  display_order?: number;
  is_active?: boolean;
}

export interface GlossaryTermUpdate {
  term?: string;
  definition?: string;
  context?: string;
  short_definition?: string;
  category_id?: string;
  display_order?: number;
  is_active?: boolean;
}

export interface GlossaryListParams {
  category_id?: string;
  letter?: string;
  limit?: number;
  offset?: number;
}

const glossaryService = {
  /**
   * List terms with filters
   * AC#1: "vejo lista alfabética de termos"
   */
  async getTerms(params?: GlossaryListParams): Promise<GlossaryTerm[]> {
    const { data } = await api.get("/glossary", { params });
    return data;
  },

  /**
   * Search terms with full-text search
   * AC#2: "posso buscar por termo"
   */
  async searchTerms(query: string, limit = 20): Promise<GlossarySearchResult[]> {
    const { data } = await api.get("/glossary/search", { params: { q: query, limit } });
    return data;
  },

  /**
   * Get term details with relations and links
   * AC#4: "termos podem ter links para artigos relacionados"
   */
  async getTermById(termId: string): Promise<GlossaryTermDetail> {
    const { data } = await api.get(`/glossary/${termId}`);
    return data;
  },

  /**
   * Get alphabet index with counters
   * AC#5: "posso navegar por letra inicial"
   */
  async getAlphabetIndex(): Promise<AlphabetIndexItem[]> {
    const { data } = await api.get("/glossary/alphabet");
    return data;
  },

  /**
   * Increment term view counter
   */
  async incrementViews(termId: string): Promise<void> {
    await api.post(`/glossary/${termId}/view`);
  },

  /**
   * List categories with term counts
   */
  async getCategories(includeInactive = false): Promise<GlossaryCategory[]> {
    const { data } = await api.get("/glossary/categories", {
      params: { include_inactive: includeInactive },
    });
    return data;
  },

  // =========================================================================
  // ADMIN METHODS
  // =========================================================================

  /**
   * Create new term (admin only)
   */
  async createTerm(termData: GlossaryTermCreate): Promise<GlossaryTerm> {
    const { data } = await api.post("/glossary", termData);
    return data;
  },

  /**
   * Update term (admin only)
   */
  async updateTerm(termId: string, termData: GlossaryTermUpdate): Promise<GlossaryTerm> {
    const { data } = await api.put(`/glossary/${termId}`, termData);
    return data;
  },

  /**
   * Delete term (soft delete, admin only)
   */
  async deleteTerm(termId: string): Promise<void> {
    await api.delete(`/glossary/${termId}`);
  },

  /**
   * Add related term (admin only)
   */
  async createRelation(
    termId: string,
    relatedTermId: string,
    relationType: "synonym" | "see_also" | "antonym"
  ): Promise<GlossaryRelation> {
    const { data } = await api.post(`/glossary/${termId}/relations`, {
      related_term_id: relatedTermId,
      relation_type: relationType,
    });
    return data;
  },

  /**
   * Add external link (admin only)
   */
  async createLink(
    termId: string,
    linkData: { title: string; url: string; link_type: "faq" | "manual" | "external" }
  ): Promise<GlossaryLink> {
    const { data } = await api.post(`/glossary/${termId}/links`, linkData);
    return data;
  },
};

export default glossaryService;
